import React, { useState } from "react";
import { Mail, X, Loader2, Check, Sticker, Building2, Sparkles } from "lucide-react";
import axios from "axios";
import { useI18n } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Modal: collects e-mail and then offers the free personalised Scan&Call
// sticker pack to companies. Never shown when the visitor arrives via a
// personalised QR id – that owner has already opted in.
export const EmailCapturePopup = ({
    onClose,
    onSubmitted,
    source = "demo_popup",
}) => {
    const { t, lang } = useI18n();

    // Stages: "email" → "sticker_offer" → "sticker_form" → "done"
    const [stage, setStage] = useState("email");

    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Sticker request state
    const [stickerForm, setStickerForm] = useState({
        companyName: "",
        contactName: "",
        street: "",
        zip: "",
        city: "",
        country: "Deutschland",
        phone: "",
        quantity: "",
        notes: "",
    });
    const updateSticker = (k) => (e) =>
        setStickerForm((s) => ({ ...s, [k]: e.target.value }));

    const closeAndMark = () => {
        if (onSubmitted) onSubmitted();
        else if (onClose) onClose();
    };

    const submitEmail = async (e) => {
        e.preventDefault();
        setError(null);
        if (!EMAIL_RE.test(email.trim())) {
            setError(t("email_popup_error_email"));
            return;
        }
        if (!consent) {
            setError(t("email_popup_error_consent"));
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/emails/subscribe`, {
                email: email.trim(),
                consent: true,
                source,
                locale: lang,
            });
            // Without QR ID: offer the free Scan&Call stickers.
            // (The popup never shows when an ID is present.)
            setStage("sticker_offer");
        } catch {
            setError(t("email_popup_error_email"));
        } finally {
            setSubmitting(false);
        }
    };

    const submitStickerRequest = async (e) => {
        e.preventDefault();
        setError(null);
        const { companyName, street, zip, city } = stickerForm;
        if (!companyName.trim() || !street.trim() || !zip.trim() || !city.trim()) {
            setError(t("sticker_error_required"));
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/sticker-requests`, {
                email: email.trim(),
                companyName: companyName.trim(),
                contactName: stickerForm.contactName.trim(),
                street: street.trim(),
                zip: zip.trim(),
                city: city.trim(),
                country: stickerForm.country.trim(),
                phone: stickerForm.phone.trim(),
                quantity: stickerForm.quantity
                    ? parseInt(stickerForm.quantity, 10)
                    : null,
                notes: stickerForm.notes.trim(),
                locale: lang,
                consent: true,
            });
            setStage("done");
            setTimeout(closeAndMark, 2000);
        } catch {
            setError(t("sticker_error_save_failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="email-popup-overlay"
            className="fixed inset-0 z-[2000] flex items-stretch justify-center bg-black/80 backdrop-blur-sm overflow-y-auto"
        >
            <div
                data-testid="email-popup"
                role="dialog"
                aria-modal="true"
                className="w-full min-h-full sm:min-h-0 sm:my-auto sm:max-w-xl bg-white sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col"
            >
                {/* Header */}
                <div className="bg-[#A72920] px-5 py-4 flex items-center justify-between gap-3 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center ring-2 ring-white/30">
                            <Mail className="w-5 h-5 text-white" />
                        </div>
                        <div className="font-display font-black text-white text-lg sm:text-xl">
                            SafetyAssist
                        </div>
                    </div>
                    <button
                        data-testid="email-popup-close"
                        onClick={onClose}
                        aria-label="Schließen"
                        className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
                    >
                        <X className="w-5 h-5" strokeWidth={2.5} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 sm:px-8 pt-6 pb-6 sm:pb-8 flex-1 overflow-y-auto">
                    {stage === "done" && (
                        <div className="flex flex-col items-center text-center py-6">
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                                <Check
                                    className="w-7 h-7 text-green-600"
                                    strokeWidth={3}
                                />
                            </div>
                            <p
                                data-testid="email-popup-success"
                                className="mt-4 font-display font-bold text-lg text-zinc-900"
                            >
                                {t("email_popup_thanks")}
                            </p>
                        </div>
                    )}

                    {stage === "email" && (
                        <form onSubmit={submitEmail} className="space-y-4">
                            <div className="mb-1">
                                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1">
                                    <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                                    {t("email_popup_eyebrow")}
                                </div>
                            </div>
                            <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-zinc-900 leading-tight">
                                {t("email_popup_title")}
                            </h3>
                            <p className="text-sm sm:text-[15px] text-zinc-600 leading-relaxed">
                                {t("email_popup_body")}
                            </p>
                            <ul className="space-y-2 text-sm sm:text-[15px] text-zinc-700">
                                <li className="flex items-start gap-2">
                                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
                                    <span>{t("email_popup_feature_1")}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
                                    <span>{t("email_popup_feature_2")}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
                                    <span>{t("email_popup_feature_3")}</span>
                                </li>
                            </ul>
                            <input
                                data-testid="email-popup-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t("email_popup_email_placeholder")}
                                required
                                className="w-full h-12 px-4 rounded-xl bg-zinc-100 border-2 border-transparent focus-visible:border-[#A72920] focus-visible:ring-0 focus-visible:outline-none font-display text-base text-zinc-900 placeholder:text-zinc-400"
                            />
                            <label
                                data-testid="email-popup-consent-label"
                                className="flex items-start gap-3 text-xs sm:text-sm text-zinc-600 cursor-pointer leading-snug"
                            >
                                <input
                                    data-testid="email-popup-consent"
                                    type="checkbox"
                                    checked={consent}
                                    onChange={(e) =>
                                        setConsent(e.target.checked)
                                    }
                                    className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-[#A72920] focus:ring-[#A72920]"
                                />
                                <span>{t("email_popup_consent")}</span>
                            </label>
                            {error && (
                                <div
                                    data-testid="email-popup-error"
                                    className="text-sm text-[#A72920] font-display font-semibold"
                                >
                                    {error}
                                </div>
                            )}
                            <button
                                data-testid="email-popup-submit"
                                type="submit"
                                disabled={submitting}
                                className="w-full h-12 rounded-xl bg-[#A72920] hover:bg-[#8e2019] disabled:opacity-60 text-white font-display font-black text-base flex items-center justify-center gap-2 transition"
                            >
                                {submitting ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    t("email_popup_submit")
                                )}
                            </button>
                            <button
                                data-testid="email-popup-later"
                                type="button"
                                onClick={onClose}
                                className="w-full text-center text-sm text-zinc-500 hover:text-zinc-900 font-display font-semibold pt-1 transition"
                            >
                                {t("email_popup_later")}
                            </button>
                        </form>
                    )}

                    {stage === "sticker_offer" && (
                        <div
                            className="space-y-4"
                            data-testid="sticker-offer"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                                    <Sticker className="w-6 h-6" />
                                </div>
                                <h3 className="font-display font-black text-xl sm:text-2xl tracking-tight text-zinc-900 leading-tight">
                                    {t("sticker_offer_title")}
                                </h3>
                            </div>
                            <p className="text-sm sm:text-[15px] text-zinc-600 leading-relaxed">
                                {t("sticker_offer_body")}
                            </p>
                            <button
                                data-testid="sticker-offer-cta"
                                type="button"
                                onClick={() => setStage("sticker_form")}
                                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-display font-black text-base flex items-center justify-center gap-2 transition"
                            >
                                {t("sticker_offer_cta")}
                            </button>
                            <button
                                data-testid="sticker-offer-skip"
                                type="button"
                                onClick={closeAndMark}
                                className="w-full text-center text-sm text-zinc-500 hover:text-zinc-900 font-display font-semibold pt-1 transition"
                            >
                                {t("sticker_offer_skip")}
                            </button>
                        </div>
                    )}

                    {stage === "sticker_form" && (
                        <form
                            onSubmit={submitStickerRequest}
                            className="space-y-3"
                            data-testid="sticker-form"
                        >
                            <div className="flex items-center gap-2 text-emerald-700">
                                <Building2 className="w-5 h-5" />
                                <h3 className="font-display font-black text-lg sm:text-xl tracking-tight text-zinc-900">
                                    {t("sticker_form_title")}
                                </h3>
                            </div>
                            <p className="text-xs text-zinc-500 leading-relaxed">
                                {t("sticker_form_body")}
                            </p>

                            <input
                                data-testid="sticker-company"
                                type="text"
                                value={stickerForm.companyName}
                                onChange={updateSticker("companyName")}
                                placeholder={t("sticker_field_company")}
                                required
                                maxLength={200}
                                className="w-full h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                            />
                            <input
                                data-testid="sticker-contact"
                                type="text"
                                value={stickerForm.contactName}
                                onChange={updateSticker("contactName")}
                                placeholder={t("sticker_field_contact")}
                                maxLength={200}
                                className="w-full h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                            />
                            <input
                                data-testid="sticker-street"
                                type="text"
                                value={stickerForm.street}
                                onChange={updateSticker("street")}
                                placeholder={t("sticker_field_street")}
                                required
                                maxLength={200}
                                className="w-full h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                            />
                            <div className="grid grid-cols-3 gap-2">
                                <input
                                    data-testid="sticker-zip"
                                    type="text"
                                    value={stickerForm.zip}
                                    onChange={updateSticker("zip")}
                                    placeholder={t("sticker_field_zip")}
                                    required
                                    maxLength={20}
                                    className="col-span-1 h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                                />
                                <input
                                    data-testid="sticker-city"
                                    type="text"
                                    value={stickerForm.city}
                                    onChange={updateSticker("city")}
                                    placeholder={t("sticker_field_city")}
                                    required
                                    maxLength={120}
                                    className="col-span-2 h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                                />
                            </div>
                            <input
                                data-testid="sticker-country"
                                type="text"
                                value={stickerForm.country}
                                onChange={updateSticker("country")}
                                placeholder={t("sticker_field_country")}
                                maxLength={80}
                                className="w-full h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <input
                                    data-testid="sticker-phone"
                                    type="tel"
                                    value={stickerForm.phone}
                                    onChange={updateSticker("phone")}
                                    placeholder={t("sticker_field_phone")}
                                    maxLength={60}
                                    className="h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                                />
                                <input
                                    data-testid="sticker-quantity"
                                    type="number"
                                    min="1"
                                    value={stickerForm.quantity}
                                    onChange={updateSticker("quantity")}
                                    placeholder={t("sticker_field_quantity")}
                                    className="h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400"
                                />
                            </div>
                            <textarea
                                data-testid="sticker-notes"
                                value={stickerForm.notes}
                                onChange={updateSticker("notes")}
                                placeholder={t("sticker_field_notes")}
                                maxLength={2000}
                                rows={2}
                                className="w-full px-3 py-2 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400 resize-none"
                            />

                            {error && (
                                <div
                                    data-testid="sticker-error"
                                    className="text-sm text-[#A72920] font-display font-semibold"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                data-testid="sticker-submit"
                                type="submit"
                                disabled={submitting}
                                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-display font-black text-base flex items-center justify-center gap-2 transition"
                            >
                                {submitting ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    t("sticker_submit")
                                )}
                            </button>
                            <button
                                data-testid="sticker-skip"
                                type="button"
                                onClick={closeAndMark}
                                className="w-full text-center text-sm text-zinc-500 hover:text-zinc-900 font-display font-semibold pt-1 transition"
                            >
                                {t("sticker_offer_skip")}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};
